import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { lineTotal, lineUnitPrice, useCart } from '../store/cart';
import { formatEuro } from '../lib/money';

/** Duración de la animación, compartida por la transición CSS y el desmontaje. */
const REVEAL_MS = 240;
/** Aire mínimo respecto al borde de la ventana al corregir el centrado. */
const EDGE_MARGIN = 12;
/** Por debajo de este ancho no hay sitio para un desplegable: se usa una capa completa. */
const MOBILE_QUERY = '(max-width: 767px)';

/** Alterna entre el desplegable anclado y la capa a pantalla completa. */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

/** Mantiene el panel montado mientras se recoge, para poder animar también el cierre. */
function useReveal(isOpen: boolean) {
  const [mounted, setMounted] = useState(isOpen);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Doble requestAnimationFrame a propósito: con uno solo, React monta el panel
      // y aplica el estado final antes de que el navegador pinte el inicial, así que
      // la transición no llega a ejecutarse y el panel aparece de golpe.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setRevealed(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setRevealed(false);
    const timer = setTimeout(() => setMounted(false), REVEAL_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  return { mounted, revealed };
}

/** Cierre con Escape y al pulsar fuera del panel. */
function useDismiss(isOpen: boolean, close: () => void, ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    const onPointerDown = (event: PointerEvent) => {
      const node = ref.current;
      if (!node) return;
      const target = event.target as HTMLElement;
      if (node.contains(target)) return;
      // El botón del carrito se ignora aquí: su propio onClick ya alterna la apertura,
      // y cerrarlo desde este manejador lo reabriría acto seguido.
      if (target.closest?.('[data-cart-toggle]')) return;
      close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen, close, ref]);
}

/** Lista de artículos, subtotal y llamada a la acción. Igual en las dos presentaciones. */
function CartContents({ onNavigate }: { onNavigate: () => void }) {
  const { lines, setQty, remove, subtotal } = useCart();

  if (lines.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-bone-300 mb-4">Aún no has añadido nada.</p>
        <Link to="/tienda" onClick={onNavigate} className="btn-primary !py-2.5 !text-sm">
          Ver catálogo
        </Link>
      </div>
    );
  }

  return (
    <>
      <ul className="max-h-[19rem] overflow-y-auto divide-y divide-ink-700">
        {lines.map((line) => (
          <li key={line.id} className="p-3.5">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-bone-50 leading-snug line-clamp-2">{line.product.name}</p>
              <button
                onClick={() => remove(line.id)}
                className="shrink-0 text-bone-400 hover:text-ember-400 transition-colors"
                aria-label={`Quitar ${line.product.name} del carrito`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M3 6h18M8 6V4h8v2m-9 0v14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V6" />
                </svg>
              </button>
            </div>

            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center rounded-full border border-ink-500">
                <button
                  onClick={() => setQty(line.id, line.qty - 1)}
                  className="px-2.5 py-1 text-bone-300 hover:text-bone-50"
                  aria-label={`Reducir cantidad de ${line.product.name}`}
                >
                  −
                </button>
                <span className="px-2 text-sm tabular-nums">{line.qty}</span>
                <button
                  onClick={() => setQty(line.id, line.qty + 1)}
                  className="px-2.5 py-1 text-bone-300 hover:text-bone-50"
                  aria-label={`Aumentar cantidad de ${line.product.name}`}
                >
                  +
                </button>
              </div>
              <span className="text-right text-sm font-medium text-bone-100 tabular-nums">
                {formatEuro(lineTotal(line))}
                {line.qty > 1 && (
                  <span className="block text-[11px] font-normal text-bone-400">
                    {formatEuro(lineUnitPrice(line))}/ud
                  </span>
                )}
              </span>
            </div>
          </li>
        ))}
      </ul>

      <div className="border-t border-ink-700 p-3.5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-bone-300">Subtotal</span>
          <span className="text-lg font-semibold tabular-nums">{formatEuro(subtotal())}</span>
        </div>
        <Link to="/checkout" onClick={onNavigate} className="btn-primary w-full !py-2.5 !text-sm">
          Continuar con la compra
        </Link>
      </div>
    </>
  );
}

/** Capa a pantalla completa sobre fondo translúcido. Presentación para móvil. */
function CartOverlay({ close }: { close: () => void }) {
  const { isOpen } = useCart();
  const { mounted, revealed } = useReveal(isOpen);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismiss(isOpen, close, panelRef);

  // Bloquea el desplazamiento del fondo mientras la capa está abierta.
  useEffect(() => {
    if (!mounted) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mounted]);

  if (!mounted) return null;

  // Se saca del Header con un portal: su `backdrop-blur` lo convierte en bloque
  // contenedor de los descendientes `fixed`, y la capa quedaba recortada a la
  // altura de la cabecera en lugar de cubrir la pantalla.
  return createPortal(
    <div
      className={`fixed inset-0 z-[80] flex items-start justify-center p-4 pt-24 bg-ink-950/70 backdrop-blur-sm transition-opacity ease-out ${
        revealed ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      style={{ transitionDuration: `${REVEAL_MS}ms` }}
    >
      <div
        ref={panelRef}
        id="cart-popover"
        role="dialog"
        aria-modal="true"
        aria-label="Resumen del carrito"
        className={`w-full max-w-sm overflow-hidden rounded-2xl border border-ink-500 bg-ink-900 shadow-elev transition-[transform,opacity] ease-out motion-reduce:transition-none ${
          revealed ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0'
        }`}
        style={{ transitionDuration: `${REVEAL_MS}ms` }}
      >
        <div className="flex items-center justify-between border-b border-ink-700 px-3.5 py-3">
          <h2 className="text-sm font-semibold tracking-tight">Tu cesta</h2>
          <button onClick={close} aria-label="Cerrar la cesta" className="text-bone-400 hover:text-bone-50 transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>
        <CartContents onNavigate={close} />
      </div>
    </div>,
    document.body,
  );
}

/** Desplegable anclado bajo el botón del carrito. Presentación para escritorio. */
function CartDropdown({ close }: { close: () => void }) {
  const { isOpen, lines } = useCart();
  const { mounted, revealed } = useReveal(isOpen);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  useDismiss(isOpen, close, wrapperRef);

  /**
   * Corrige el centrado lo justo para que el panel no se salga de la ventana.
   * Se calcula desde el ancla (el contenedor del botón, que no se desplaza) y no
   * desde el propio panel: medir el panel ya corregido daría «no hace falta
   * corrección» y el desplazamiento se reiniciaría a cero en cada recálculo.
   */
  useLayoutEffect(() => {
    if (!mounted) return;

    const adjust = () => {
      const wrapper = wrapperRef.current;
      const anchor = wrapper?.parentElement;
      if (!wrapper || !anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const center = anchorRect.left + anchorRect.width / 2;
      const half = wrapper.offsetWidth / 2;
      const viewport = document.documentElement.clientWidth;

      if (center + half > viewport - EDGE_MARGIN) {
        setShift(viewport - EDGE_MARGIN - (center + half));
      } else if (center - half < EDGE_MARGIN) {
        setShift(EDGE_MARGIN - (center - half));
      } else {
        setShift(0);
      }
    };

    adjust();
    window.addEventListener('resize', adjust);
    return () => window.removeEventListener('resize', adjust);
  }, [mounted, lines.length]);

  if (!mounted) return null;

  return (
    <div
      ref={wrapperRef}
      className="absolute left-1/2 top-full z-50 mt-2 w-[min(20rem,calc(100vw-1.5rem))]"
      style={{ transform: `translateX(calc(-50% + ${shift}px))` }}
    >
      {/* La persiana se hace con clip-path: recorta sin deformar el contenido,
          a diferencia de un scaleY. El transform queda libre para el centrado. */}
      <div
        id="cart-popover"
        role="dialog"
        aria-label="Resumen del carrito"
        className={`overflow-hidden rounded-2xl border border-ink-500 bg-ink-900 shadow-elev transition-[clip-path,opacity] ease-out motion-reduce:transition-none ${
          revealed ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        style={{
          clipPath: revealed ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)',
          transitionDuration: `${REVEAL_MS}ms`,
        }}
      >
        <CartContents onNavigate={close} />
      </div>
    </div>
  );
}

export default function CartPopover() {
  const close = useCart((s) => s.close);
  const isMobile = useIsMobile();

  return isMobile ? <CartOverlay close={close} /> : <CartDropdown close={close} />;
}
